import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { mockData } from '../mock';
import ChartCard from '../components/ChartCard';
import PeriodFilter from '../components/PeriodFilter';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../components/ui/table';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { subDays, isAfter, isBefore, parseISO, format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { Droplets, RefreshCw, Wifi, WifiOff, Activity, Filter, Search, Eye, EyeOff, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cores para os dispositivos
const DEVICE_COLORS = ['#06b6d4', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b'];

// Função para formatar números com vírgula como separador decimal
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(decimals).replace('.', ',');
};

const Agua = () => {
  const [periodFilter, setPeriodFilter] = useState({ type: 'preset', days: 7 });
  const [loading, setLoading] = useState(false);
  const [apiConnected, setApiConnected] = useState(false);
  const [realTimeData, setRealTimeData] = useState(null);
  const [consumosData, setConsumosData] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [deviceConfigs, setDeviceConfigs] = useState([]);
  
  // Filtros da tabela
  const [filterLocal, setFilterLocal] = useState('all');
  const [filterDevice, setFilterDevice] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dispositivos visíveis no gráfico (seleção múltipla)
  const [visibleDevices, setVisibleDevices] = useState(new Set());

  // Carregar configurações de dispositivos
  const loadDeviceConfigs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/configuracoes/dispositivos?categoria=agua`);
      const data = await response.json();
      setDeviceConfigs(data || []);
      // Inicializar todos os dispositivos como visíveis
      if (data && data.length > 0) {
        setVisibleDevices(new Set(data.map(d => d.device_id)));
      }
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      return [];
    }
  }, []);

  // Carregar dados em tempo real
  const loadRealTimeData = useCallback(async () => {
    setLoading(true);
    try {
      const testResponse = await fetch(`${API_URL}/api/agua/test-connection`);
      const testData = await testResponse.json();
      setApiConnected(testData.success);

      if (testData.success) {
        const resumoResponse = await fetch(`${API_URL}/api/agua/resumo`);
        const resumoData = await resumoResponse.json();
        
        if (resumoData.success) {
          setRealTimeData(resumoData);
          setLastUpdate(new Date());
        }

        const configs = await loadDeviceConfigs();
        const dias = periodFilter.type === 'preset' ? periodFilter.days : 30;
        
        const consumosPromises = configs.map(async (config) => {
          const response = await fetch(`${API_URL}/api/agua/consumos/${config.device_id}?dias=${dias}`);
          const data = await response.json();
          return { deviceId: config.device_id, ...data };
        });

        const consumosResults = await Promise.all(consumosPromises);
        const consumosMap = {};
        consumosResults.forEach(result => {
          consumosMap[result.deviceId] = result;
        });
        setConsumosData(consumosMap);

        toast.success('Dados atualizados com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setApiConnected(false);
      toast.error('Erro ao carregar dados da API');
    }
    setLoading(false);
  }, [periodFilter, loadDeviceConfigs]);

  useEffect(() => {
    loadRealTimeData();
  }, [loadRealTimeData]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadRealTimeData();
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadRealTimeData]);

  // Toggle visibilidade de um dispositivo
  const toggleDeviceVisibility = (deviceId) => {
    setVisibleDevices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        // Não permitir desselecionar todos
        if (newSet.size > 1) {
          newSet.delete(deviceId);
        }
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  };

  // Selecionar apenas um dispositivo
  const selectOnlyDevice = (deviceId) => {
    setVisibleDevices(new Set([deviceId]));
  };

  // Selecionar todos os dispositivos
  const selectAllDevices = () => {
    setVisibleDevices(new Set(deviceConfigs.map(d => d.device_id)));
  };

  // Obter cor do dispositivo
  const getDeviceColor = (deviceId) => {
    const index = deviceConfigs.findIndex(c => c.device_id === deviceId);
    return DEVICE_COLORS[index % DEVICE_COLORS.length];
  };

  // Locais únicos para filtro
  const uniqueLocals = useMemo(() => {
    const locals = new Set(deviceConfigs.map(c => c.local));
    return Array.from(locals);
  }, [deviceConfigs]);

  // Dados para gráficos
  const chartData = useMemo(() => {
    if (!apiConnected || Object.keys(consumosData).length === 0) {
      if (periodFilter.type === 'preset') {
        const cutoffDate = subDays(new Date(), periodFilter.days);
        const filtered = mockData.agua.filter(item => {
          const itemDate = parseISO(item.data);
          return isAfter(itemDate, cutoffDate);
        });
        
        const grouped = {};
        filtered.forEach(item => {
          if (!grouped[item.data]) {
            grouped[item.data] = { data: item.data };
          }
          grouped[item.data][item.local] = item.consumo;
        });
        return Object.values(grouped);
      }
      return [];
    }

    const dailyData = {};
    
    Object.entries(consumosData).forEach(([deviceId, deviceData]) => {
      const config = deviceConfigs.find(c => c.device_id === deviceId);
      const descricao = config?.descricao || deviceId;
      
      if (deviceData.consumos) {
        deviceData.consumos.forEach(consumo => {
          const date = consumo.timestamp.split('T')[0];
          if (!dailyData[date]) {
            dailyData[date] = { data: date };
          }
          if (!dailyData[date][descricao]) {
            dailyData[date][descricao] = 0;
          }
          dailyData[date][descricao] += (consumo.valor_litros || 0) / 1000;
        });
      }
    });

    return Object.values(dailyData).sort((a, b) => a.data.localeCompare(b.data));
  }, [apiConnected, consumosData, deviceConfigs, periodFilter]);

  // Dados por dispositivo para cards
  const deviceStats = useMemo(() => {
    if (!realTimeData?.por_local) return [];

    const stats = [];
    realTimeData.por_local.forEach(local => {
      local.dispositivos.forEach(device => {
        const config = deviceConfigs.find(c => c.device_id === device.device);
        const consumoData = consumosData[device.device];
        
        let consumoPeriodo = 0;
        if (consumoData?.consumos) {
          consumoPeriodo = consumoData.consumos.reduce((sum, c) => sum + (c.valor_litros || 0), 0) / 1000;
        }

        stats.push({
          device: device.device,
          descricao: config?.descricao || device.device,
          local: local.local,
          leituraAtual: device.valor_m3,
          consumoPeriodo: consumoPeriodo.toFixed(2),
          totalRegistos: consumoData?.total || 0
        });
      });
    });
    
    return stats;
  }, [realTimeData, deviceConfigs, consumosData]);

  // Dados da tabela com local e dispositivo
  const tableData = useMemo(() => {
    if (!apiConnected) {
      if (periodFilter.type === 'preset') {
        const cutoffDate = subDays(new Date(), periodFilter.days);
        return mockData.agua.filter(item => {
          const itemDate = parseISO(item.data);
          return isAfter(itemDate, cutoffDate);
        }).map(item => ({
          ...item,
          dispositivo: 'Mock Device',
          deviceId: 'mock'
        }));
      }
      return mockData.agua.map(item => ({
        ...item,
        dispositivo: 'Mock Device',
        deviceId: 'mock'
      }));
    }

    const rows = [];
    Object.entries(consumosData).forEach(([deviceId, deviceData]) => {
      const config = deviceConfigs.find(c => c.device_id === deviceId);
      
      if (deviceData.consumos) {
        // Agregar consumos por dia
        const dailyConsumo = {};
        deviceData.consumos.forEach(consumo => {
          const date = consumo.timestamp.split('T')[0];
          if (!dailyConsumo[date]) {
            dailyConsumo[date] = 0;
          }
          dailyConsumo[date] += (consumo.valor_litros || 0) / 1000;
        });

        // Ordenar datas para calcular desvio vs dia anterior
        const sortedDates = Object.keys(dailyConsumo).sort();
        
        sortedDates.forEach((date, index) => {
          const consumo = dailyConsumo[date];
          let desvio = 0;
          
          // Calcular desvio em relação ao dia anterior (mesmo dispositivo)
          if (index > 0) {
            const previousDate = sortedDates[index - 1];
            const previousConsumo = dailyConsumo[previousDate];
            if (previousConsumo > 0) {
              desvio = ((consumo - previousConsumo) / previousConsumo) * 100;
            } else if (consumo > 0) {
              desvio = 100; // Se dia anterior era 0 e hoje tem consumo
            }
          }
          // Se for o primeiro dia, desvio = 0 (sem referência)
          
          rows.push({
            data: date,
            local: config?.local || 'N/A',
            dispositivo: config?.descricao || deviceId,
            deviceId: deviceId,
            consumo: parseFloat(consumo.toFixed(3)),
            desvio: parseFloat(desvio.toFixed(1))
          });
        });
      }
    });

    return rows.sort((a, b) => b.data.localeCompare(a.data));
  }, [apiConnected, consumosData, deviceConfigs, periodFilter]);

  // Dados filtrados da tabela
  const filteredTableData = useMemo(() => {
    return tableData.filter(row => {
      const matchLocal = filterLocal === 'all' || row.local === filterLocal;
      const matchDevice = filterDevice === 'all' || row.deviceId === filterDevice;
      const matchSearch = searchTerm === '' || 
        row.local.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.dispositivo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.data.includes(searchTerm);
      
      return matchLocal && matchDevice && matchSearch;
    });
  }, [tableData, filterLocal, filterDevice, searchTerm]);

  // Totalizadores
  const totals = useMemo(() => {
    if (filteredTableData.length === 0) {
      return { consumoTotal: 0, registos: 0 };
    }

    const consumoTotal = filteredTableData.reduce((sum, row) => sum + row.consumo, 0);

    return {
      consumoTotal: formatNumber(consumoTotal, 3),
      registos: filteredTableData.length
    };
  }, [filteredTableData]);

  // Funções de Exportação
  const exportToCSV = () => {
    if (filteredTableData.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    const headers = ['Data', 'Local', 'Dispositivo', 'Consumo (m³)', 'Desvio (%)'];
    const rows = filteredTableData.map(row => [
      format(new Date(row.data), 'dd/MM/yyyy'),
      row.local,
      row.dispositivo,
      row.consumo.toFixed(3).replace('.', ','),
      `${row.desvio > 0 ? '+' : ''}${row.desvio.toFixed(1).replace('.', ',')}%`
    ]);

    // Adicionar linha de total
    rows.push(['', '', 'TOTAL', totals.consumoTotal + ' m³', '']);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `consumo_agua_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Ficheiro CSV exportado com sucesso!');
  };

  const exportToExcel = () => {
    if (filteredTableData.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    // Criar HTML table para Excel
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head><meta charset="UTF-8">
      <style>
        table { border-collapse: collapse; }
        th, td { border: 1px solid #000; padding: 8px; }
        th { background-color: #003D7A; color: white; font-weight: bold; }
        .total { background-color: #e5e7eb; font-weight: bold; }
        .number { text-align: right; }
        .positive { color: #dc2626; }
        .negative { color: #16a34a; }
      </style>
      </head><body>
      <h2>Relatório de Consumo de Água</h2>
      <p>Data de exportação: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
      <p>Período: Últimos ${periodFilter.days} dias</p>
      <p>Total de registos: ${filteredTableData.length}</p>
      <table>
        <thead>
          <tr>
            <th>Data</th>
            <th>Local</th>
            <th>Dispositivo</th>
            <th>Consumo (m³)</th>
            <th>Desvio (%)</th>
          </tr>
        </thead>
        <tbody>
    `;

    filteredTableData.forEach(row => {
      const desvioClass = row.desvio > 0 ? 'positive' : row.desvio < 0 ? 'negative' : '';
      html += `
        <tr>
          <td>${format(new Date(row.data), 'dd/MM/yyyy')}</td>
          <td>${row.local}</td>
          <td>${row.dispositivo}</td>
          <td class="number">${row.consumo.toFixed(3).replace('.', ',')}</td>
          <td class="number ${desvioClass}">${row.desvio > 0 ? '+' : ''}${row.desvio.toFixed(1).replace('.', ',')}%</td>
        </tr>
      `;
    });

    html += `
        <tr class="total">
          <td colspan="3" style="text-align: right;">TOTAL (${totals.registos} registos):</td>
          <td class="number">${totals.consumoTotal} m³</td>
          <td></td>
        </tr>
        </tbody>
      </table>
      </body></html>
    `;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `consumo_agua_${format(new Date(), 'yyyy-MM-dd')}.xls`;
    link.click();
    toast.success('Ficheiro Excel exportado com sucesso!');
  };

  const exportToPDF = () => {
    if (filteredTableData.length === 0) {
      toast.error('Não há dados para exportar');
      return;
    }

    // Criar HTML para impressão/PDF
    const printWindow = window.open('', '_blank');
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Relatório de Consumo de Água</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: Arial, sans-serif; font-size: 12px; color: #333; }
          h1 { color: #003D7A; font-size: 18px; margin-bottom: 5px; }
          h2 { color: #666; font-size: 14px; font-weight: normal; margin-top: 0; }
          .header { border-bottom: 2px solid #003D7A; padding-bottom: 10px; margin-bottom: 20px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 20px; color: #666; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background-color: #003D7A; color: white; padding: 10px 8px; text-align: left; font-size: 11px; }
          td { padding: 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .number { text-align: right; font-family: monospace; }
          .positive { color: #dc2626; }
          .negative { color: #16a34a; }
          .total-row { background-color: #e5e7eb !important; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #999; font-size: 10px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Infraestruturas de Portugal</h1>
          <h2>Relatório de Consumo de Água</h2>
        </div>
        <div class="meta">
          <span>Data de exportação: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</span>
          <span>Período: Últimos ${periodFilter.days} dias</span>
          <span>Total: ${filteredTableData.length} registos</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Local</th>
              <th>Dispositivo</th>
              <th style="text-align: right;">Consumo (m³)</th>
              <th style="text-align: right;">Desvio (%)</th>
            </tr>
          </thead>
          <tbody>
    `;

    filteredTableData.forEach(row => {
      const desvioClass = row.desvio > 0 ? 'positive' : row.desvio < 0 ? 'negative' : '';
      html += `
        <tr>
          <td>${format(new Date(row.data), 'dd/MM/yyyy')}</td>
          <td>${row.local}</td>
          <td>${row.dispositivo}</td>
          <td class="number">${row.consumo.toFixed(3).replace('.', ',')}</td>
          <td class="number ${desvioClass}">${row.desvio > 0 ? '+' : ''}${row.desvio.toFixed(1).replace('.', ',')}%</td>
        </tr>
      `;
    });

    html += `
          <tr class="total-row">
            <td colspan="3" style="text-align: right;">TOTAL (${totals.registos} registos):</td>
            <td class="number">${totals.consumoTotal} m³</td>
            <td></td>
          </tr>
          </tbody>
        </table>
        <div class="footer">
          Documento gerado automaticamente pela plataforma SmartInfra IP
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    toast.success('A preparar PDF para impressão...');
  };

  // Custom Legend Click Handler
  const handleLegendClick = (data) => {
    const config = deviceConfigs.find(c => (c.descricao || c.device_id) === data.value);
    if (config) {
      toggleDeviceVisibility(config.device_id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Droplets className="h-8 w-8 text-cyan-500" />
            Água
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-gray-600">Consumo de água em tempo real</p>
            {apiConnected ? (
              <Badge className="bg-green-100 text-green-800">
                <Wifi className="h-3 w-3 mr-1" />
                API Conectada
              </Badge>
            ) : (
              <Badge className="bg-yellow-100 text-yellow-800">
                <WifiOff className="h-3 w-3 mr-1" />
                Dados Simulados
              </Badge>
            )}
          </div>
          {lastUpdate && (
            <p className="text-xs text-gray-500 mt-1">
              Última atualização: {format(lastUpdate, "dd/MM/yyyy HH:mm:ss", { locale: pt })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={loadRealTimeData} 
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <PeriodFilter 
            value={periodFilter.type === 'preset' ? periodFilter.days.toString() : 'custom'}
            onChange={setPeriodFilter}
          />
        </div>
      </div>

      {/* Totalizadores por Dispositivo - Clicáveis */}
      {apiConnected && deviceStats.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Clique nos cartões para filtrar o gráfico (seleção múltipla)
            </p>
            {visibleDevices.size < deviceConfigs.length && (
              <Button variant="ghost" size="sm" onClick={selectAllDevices}>
                Mostrar todos
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {deviceStats.map((stat, index) => {
              const isVisible = visibleDevices.has(stat.device);
              const color = getDeviceColor(stat.device);
              
              return (
                <Card 
                  key={stat.device} 
                  className={`overflow-hidden cursor-pointer transition-all duration-200 ${
                    isVisible 
                      ? 'ring-2 shadow-md' 
                      : 'opacity-50 grayscale hover:opacity-75'
                  }`}
                  style={{ 
                    borderColor: isVisible ? color : undefined,
                    '--tw-ring-color': color 
                  }}
                  onClick={() => toggleDeviceVisibility(stat.device)}
                  onDoubleClick={() => selectOnlyDevice(stat.device)}
                >
                  <div 
                    className="h-2 transition-all duration-200"
                    style={{ backgroundColor: isVisible ? color : '#d1d5db' }}
                  ></div>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-500">{stat.descricao}</p>
                          {isVisible ? (
                            <Eye className="h-3 w-3 text-green-500" />
                          ) : (
                            <EyeOff className="h-3 w-3 text-gray-400" />
                          )}
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{formatNumber(stat.leituraAtual, 0)} m³</p>
                        <p className="text-xs text-gray-400">Leitura atual</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-cyan-100 text-cyan-800">{stat.local}</Badge>
                        <p className="text-sm font-semibold text-cyan-600 mt-2">
                          +{formatNumber(stat.consumoPeriodo, 2)} m³
                        </p>
                        <p className="text-xs text-gray-400">Período</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Total Geral */}
      {apiConnected && realTimeData && (
        <Card className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-sm">Total Geral (Leitura Atual)</p>
                <p className="text-4xl font-bold">{realTimeData.total_geral.m3.toLocaleString('pt-PT')} m³</p>
                <p className="text-cyan-200 text-sm">{realTimeData.total_geral.litros.toLocaleString('pt-PT')} litros</p>
              </div>
              <div className="text-right">
                <p className="text-cyan-100 text-sm">Data dos dados</p>
                <p className="text-xl font-semibold">{realTimeData.data}</p>
                <p className="text-cyan-200 text-sm">{realTimeData.por_local?.length || 0} locais | {deviceStats.length} contadores</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráfico de Consumo */}
      {chartData.length > 0 && (
        <ChartCard title={`Consumo de Água - Últimos ${periodFilter.days} dias`}>
          <div className="mb-2 text-xs text-gray-500 flex items-center gap-1">
            <span>💡 Clique na legenda para mostrar/ocultar dispositivos</span>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <defs>
                {deviceConfigs.map((config, index) => (
                  <linearGradient key={config.device_id} id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={DEVICE_COLORS[index % DEVICE_COLORS.length]} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={DEVICE_COLORS[index % DEVICE_COLORS.length]} stopOpacity={0.1}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="data" 
                tick={{ fontSize: 11 }} 
                stroke="#6b7280"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return format(date, 'dd/MM', { locale: pt });
                }}
              />
              <YAxis tick={{ fontSize: 11 }} stroke="#6b7280" unit=" m³" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}
                formatter={(value) => [`${Number(value).toFixed(3)} m³`, '']}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px', cursor: 'pointer' }}
                onClick={handleLegendClick}
                formatter={(value, entry) => {
                  const config = deviceConfigs.find(c => (c.descricao || c.device_id) === value);
                  const isVisible = config ? visibleDevices.has(config.device_id) : true;
                  return (
                    <span style={{ 
                      color: isVisible ? entry.color : '#9ca3af',
                      textDecoration: isVisible ? 'none' : 'line-through',
                      opacity: isVisible ? 1 : 0.5
                    }}>
                      {value}
                    </span>
                  );
                }}
              />
              {deviceConfigs.map((config, index) => {
                const isVisible = visibleDevices.has(config.device_id);
                return (
                  <Area 
                    key={config.device_id}
                    type="monotone" 
                    dataKey={config.descricao || config.device_id} 
                    stroke={DEVICE_COLORS[index % DEVICE_COLORS.length]} 
                    fillOpacity={isVisible ? 1 : 0}
                    fill={`url(#color${index})`}
                    strokeWidth={isVisible ? 2 : 0}
                    strokeOpacity={isVisible ? 1 : 0}
                    hide={!isVisible}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Tabela de Consumos com Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Header da tabela */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="h-5 w-5 text-cyan-500" />
                Consumos Diários de Água
                {apiConnected && <Badge className="ml-2 bg-green-100 text-green-800">Dados Reais</Badge>}
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-500">{filteredTableData.length} registos</span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={filteredTableData.length === 0}>
                      <Download className="h-4 w-4 mr-2" />
                      Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportToCSV} className="cursor-pointer">
                      <FileText className="h-4 w-4 mr-2 text-green-600" />
                      Exportar CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToExcel} className="cursor-pointer">
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                      Exportar Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={exportToPDF} className="cursor-pointer">
                      <FileText className="h-4 w-4 mr-2 text-red-600" />
                      Exportar PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Pesquisar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterLocal} onValueChange={setFilterLocal}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os locais" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os locais</SelectItem>
                  {uniqueLocals.map(local => (
                    <SelectItem key={local} value={local}>{local}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterDevice} onValueChange={setFilterDevice}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os dispositivos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os dispositivos</SelectItem>
                  {deviceConfigs.map(config => (
                    <SelectItem key={config.device_id} value={config.device_id}>
                      {config.descricao || config.device_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tabela */}
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Data</TableHead>
                    <TableHead className="font-semibold">Local</TableHead>
                    <TableHead className="font-semibold">Dispositivo</TableHead>
                    <TableHead className="font-semibold text-right">Consumo (m³)</TableHead>
                    <TableHead className="font-semibold text-right">Desvio (%)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTableData.length > 0 ? (
                    filteredTableData.map((row, index) => (
                      <TableRow key={`${row.deviceId}-${row.data}-${index}`} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {format(new Date(row.data), 'dd/MM/yyyy', { locale: pt })}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-cyan-100 text-cyan-800">{row.local}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getDeviceColor(row.deviceId) }}
                            ></div>
                            {row.dispositivo}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(row.consumo, 3)} m³</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${row.desvio > 0 ? 'text-red-600' : row.desvio < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {row.desvio > 0 ? '+' : ''}{formatNumber(row.desvio, 1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        Nenhum registo encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                {filteredTableData.length > 0 && (
                  <TableFooter>
                    <TableRow className="bg-gray-100 font-semibold">
                      <TableCell colSpan={3} className="text-right">
                        Total ({totals.registos} registos):
                      </TableCell>
                      <TableCell className="text-right font-mono text-cyan-700">
                        {totals.consumoTotal} m³
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Agua;
